import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, AutomationInterface, Comparison, Rule, WorkflowStepInterface } from '@vendasta/automata';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MultiBranchNodeData } from '../../../../common/multi-node-branch-data';
import { BranchUtilities } from '../../../branch-utilities';
import { RIGHT } from '../../../component-loader/branch-options';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';
import { FilterDataService } from '../../filters/filter-data/filter-data-selector/filter-data.service';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example AddTagService
 */
@Injectable({ providedIn: 'root' })
export class IfElseBranchService implements CommonDisplayActivityService, CommonDisplayActionService {
  constructor(
    private readonly translateService: TranslateService,
    public sanitizer: DomSanitizer,
    private filterDataService: FilterDataService,
  ) {}

  getActivitySubtitle(
    _namespace: string,
    activity: ActivityInterface,
    automation: AutomationInterface,
  ): Observable<string> {
    const nextStep = activity?.nextWorkflowStep;

    const nextStepId = nextStep?.id;

    let displayContent = of('');

    if (!nextStepId) {
      displayContent = this.translateService.stream(
        'AUTOMATIONS.EDITOR.TASKS.IF_ELSE_BRANCH.ACTIVITY.COMPLETED_AUTOMATION',
      );
    } else {
      const ruleDisplays = this.getRuleDisplays(automation, activity?.workflowStep);
      const branchTaken = (activity?.workflowStep?.choices?.rules || []).find((r) => r.next === nextStepId);
      const indexOfBranch = (activity?.workflowStep?.choices?.rules || []).indexOf(branchTaken);
      if (ruleDisplays[indexOfBranch]) {
        displayContent = ruleDisplays[indexOfBranch].pipe(
          map((displayValue) => {
            let ruleDisplay = displayValue;
            if (ruleDisplays.length > 1) {
              ruleDisplay = indexOfBranch + 1 + '. ' + displayValue;
            }
            return ruleDisplay;
          }),
        );
      }
    }

    return displayContent;
  }

  getActivityTitle(_namespace: string, activity: ActivityInterface): Observable<string> {
    const nextStep = activity?.nextWorkflowStep;
    const executedStep = activity?.workflowStep;

    const nextStepId = nextStep?.id;

    let branchName;
    if (!nextStep) {
      const nextStepIndex = activity?.nextWorkflowStepBranchTakenIndex || 0;
      const [key, interpolationParams] = BranchUtilities.getBranchTextKey(executedStep, nextStepIndex);
      branchName = this.translateService.instant(key, interpolationParams);
    } else if (executedStep.choices?.defaultNextStep === nextStepId) {
      const [key, interpolationParams] = BranchUtilities.getBranchTextKey(executedStep, RIGHT);
      branchName = this.translateService.instant(key, interpolationParams);
    } else {
      const branchTaken = (executedStep?.choices?.rules || []).find((r) => r.next === nextStepId);
      const indexOfBranch = (executedStep?.choices?.rules || []).indexOf(branchTaken);
      const [key, interpolationParams] = BranchUtilities.getBranchTextKey(executedStep, indexOfBranch);
      branchName = this.translateService.instant(key, interpolationParams);
    }

    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.IF_ELSE_BRANCH.ACTIVITY.CONTINUED_ON_BRANCH', {
      branch: branchName,
    });
  }

  getTemplateActionSubtitle(
    namespace: string,
    step: WorkflowStepInterface,
    isWaitUntil: boolean,
    automation: AutomationInterface,
  ): Observable<string> {
    return this.getActionSubtitle(namespace, step, isWaitUntil, automation);
  }

  getActionSubtitle(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntil: boolean,
    automation: AutomationInterface,
  ): Observable<string> {
    if (step?.choices?.rules?.length === 0) {
      //not sure how to handle no rules
      return of(this.translateService.instant('AUTOMATIONS.ERRORS.ERROR_DISPLAY'));
    }

    const ruleDisplays = this.getRuleDisplays(automation, step);
    return forkJoin(...ruleDisplays).pipe(
      map((displayValues) => {
        return displayValues.reduce((acc, curr, i, arr) => {
          if (arr.length > 1) {
            const num = i + 1;
            const label = this.translateService.instant('AUTOMATIONS.EDITOR.BRANCH.BRANCH_NUMBER', {
              number: num,
            });
            if (i === 0) {
              return label + '. ' + curr;
            }
            return acc + '<hr>' + label + '. ' + curr;
          }
          return acc + curr;
        }, '');
      }),
    );
  }

  getTemplateMultiBranchNodeData(
    namespace: string,
    step: WorkflowStepInterface,
    automation: AutomationInterface,
    isWaitUntil?: boolean,
  ): Observable<MultiBranchNodeData[]> {
    return this.getMultiBranchNodeData(namespace, step, automation, isWaitUntil);
  }

  getMultiBranchNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    automation: AutomationInterface,
    _isWaitUntil?: boolean,
  ): Observable<MultiBranchNodeData[]> {
    if (step?.choices?.rules?.length === 0) {
      //not sure how to handle no rules
      return of([]);
    }

    const ruleDisplays = this.getRuleDisplays(automation, step);
    return forkJoin(...ruleDisplays).pipe(
      map((displayValues) =>
        displayValues.map((dv, index) => {
          const [key, interpolationParams] = BranchUtilities.getBranchTextKey(step, index);
          const branchName = this.translateService.instant(key, interpolationParams);
          return {
            branchName: branchName,
            branchDisplay: dv,
          };
        }),
      ),
    );
  }

  getActionSubtitleSuccessState(_data: any): Observable<string> {
    return of('');
  }

  getActionSubtitleErrorState(_data: any, _step: WorkflowStepInterface): Observable<string> {
    return of('');
  }

  getActionSubtitleEmptyState(_step: WorkflowStepInterface): Observable<string> {
    return of('');
  }

  getRuleDisplays(automation: AutomationInterface, step: WorkflowStepInterface): Observable<string>[] {
    return step?.choices?.rules.map((rule) => {
      const andOr = Rule.fromRuleInterface(rule);
      const rules = andOr.value as Rule[];
      if (rules.length < 1) {
        //Not sure how to handle multiple filters yet
        return this.translateService.stream('AUTOMATIONS.ERRORS.ERROR_DISPLAY');
      }
      return forkJoin([
        ...rules.map((r) => this.filterDataService.ruleDisplay(automation, r, step.taskDefinitionId, step.id)),
      ]).pipe(
        catchError((err) => {
          if (err.status === 404) {
            return of([
              this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.IF_ELSE_BRANCH.DELETED_PREVIOUS_STEP_COMPARISON'),
            ]);
          }
          throwError(err);
        }),
        map((rd: string[]) => {
          let comparisonKey = 'AUTOMATIONS.EDITOR.FILTERS.COMPARISON.AND';
          if (andOr.comparison === Comparison.Or) {
            comparisonKey = 'AUTOMATIONS.EDITOR.FILTERS.COMPARISON.OR';
          }
          return rd.join('<br />' + this.translateService.instant(comparisonKey) + '<br />');
        }),
      );
    });
  }
}
